import {
  Group,
  PerspectiveCamera,
  Raycaster,
  Vector2,
  Vector3,
  type Object3D,
  type Scene,
} from 'three';
import { Capsule } from 'three/examples/jsm/math/Capsule.js';
import type { Octree } from 'three/examples/jsm/math/Octree.js';
import type { HingeDef } from '../hinges';
import { useHingesStore } from '@/state/hingesStore';
import { useUiStore, type EgoInteractHint } from '@/state/uiStore';

/**
 * First-person controller for ego mode.
 *
 * Owns the ego camera, a capsule collider, keyboard / pointer-locked
 * mouse input, a crosshair raycaster for interactables, and the physics
 * step (gravity + capsule-vs-octree). The controller does NOT own the
 * Octree itself — the viewport rebuilds it on house / hinge changes and
 * hands it in via `setOctree`, which keeps the rebuild cadence policy
 * in one place and the controller's state minimal.
 *
 * Lifecycle: one instance per viewport, activated and deactivated
 * through `enter()` / `exit()`.
 */

/**
 * Capsule dimensions. The generator builds EXTERIOR door openings at
 * 2.1 m outer height, but subtract the frame thickness (≈ 0.06 m) and
 * the actual clear head room is only ~2.04 m. INTERIOR doors are a
 * 2.05 m outer height with a 0.04 m jamb header, giving just 2.01 m
 * clear. On top of that, the ground-floor slab sits ~0.4 m ABOVE the
 * outdoor grade (plinth), so walking through the front door is a
 * step-up — the capsule briefly rises by the plinth height during the
 * step-up sweep (see `moveAndResolve`) before gravity drops it onto
 * the raised floor. We leave extra headroom so that the lifted
 * capsule still clears an interior door header (1.97 m ≤ 2.01 m):
 *
 *   crown = feet + radius + cylinder + radius
 *         = 0   + 0.27   + 1.03     + 0.27   = 1.57 m   (> 0.44 m margin)
 *   eye   = feet + radius + cylinder
 *         = 0   + 0.27   + 1.03              = 1.30 m   (standing eye)
 *
 * Diameter 0.54 m fits through a ~0.78 m partition-door opening with
 * ~12 cm of clearance per side. Shorter than a real adult, but ego
 * mode is for SPATIAL reading of the generated house — we'd rather
 * err on the side of "always fits" than "accurate eye height".
 */
const CAPSULE_RADIUS = 0.27;
const CYLINDER_HEIGHT = 1.03;

const WALK_SPEED = 2.8;
const SPRINT_MULT = 1.8;
const GRAVITY = -20;
const MAX_FALL = -55;
/** Initial vertical velocity on Space press. With GRAVITY=-20 this
 *  gives an apex of ≈ 0.9 m — enough to hop onto a kitchen counter
 *  or clear a fence rail without feeling like a moon jump. */
const JUMP_IMPULSE = 6.0;
/** Grace period (seconds) after leaving the ground during which a
 *  Space press still triggers a jump. See `timeSinceGrounded`. */
const COYOTE_TIME = 0.15;
/** Maximum obstacle height the horizontal collision solver will
 *  auto-climb in a single sweep (lift capsule, reapply horizontal
 *  motion, let gravity drop us onto the step top on the next frame).
 *  House stair risers are around 0.18 m, so 0.4 m gives comfortable
 *  head-room without letting the player wall-climb arbitrary walls. */
const STEP_HEIGHT = 0.4;
/** Minimum horizontal distance the step-up sweep probes forward,
 *  regardless of this frame's walking motion. When a grounded capsule
 *  is stopped by an obstacle, the sphere centre sits roughly one
 *  capsule-radius BEHIND the obstacle's leading edge — the sphere
 *  surface kisses it. One frame of walking (~0.05 m) is nowhere near
 *  enough to cross that edge. If we sweep down from there we'd land
 *  back on the original floor and cancel the climb, producing the
 *  "jiggle in front of a stair" the user reported. We instead probe
 *  at least just over one capsule-radius forward, which guarantees
 *  the sphere centre crosses the edge of any obstacle the capsule
 *  radius is even touching. Legitimate walls still reject this probe
 *  via `pushOutOfGeometry(floorsOnly=true)` bailing on horizontal
 *  normals, so this does NOT let the player wall-hop. */
const STEP_PROBE_DIST = CAPSULE_RADIUS + 0.07;
// How far in front of the camera we poke for hinge interaction. Long
// enough to catch a door you're a step away from, short enough that
// you never light up a hinge on the far side of the room.
const INTERACT_REACH = 3.0;
const PITCH_LIMIT = (Math.PI / 2) * 0.95;

export interface EgoSpawn {
  /** Feet-level position in world space. */
  x: number;
  y: number;
  z: number;
  /** World-space yaw (radians). 0 faces -Z. */
  yaw: number;
}

export class EgoController {
  readonly camera: PerspectiveCamera;
  private readonly domElement: HTMLElement;
  private readonly scene: Scene;

  active = false;
  private octree: Octree | null = null;

  private readonly capsule = new Capsule(new Vector3(), new Vector3(), CAPSULE_RADIUS);
  private readonly velocity = new Vector3();
  private onGround = false;
  /**
   * Seconds since we last had a positively-grounded contact. Enables
   * "coyote time": the player can still jump for ~150 ms AFTER
   * leaving a ledge or between the micro-airborne frames that show up
   * on uneven ground (terrace plinth, between stair treads). Without
   * this, jump attempts right at the moment the player steps off an
   * edge, or while walking up any slope that oscillates `onGround` by
   * a frame, silently do nothing — which is the "Space doesn't seem
   * to do anything" behaviour the user reported.
   */
  private timeSinceGrounded = Number.POSITIVE_INFINITY;

  // Keyboard state. We keep both physical (KeyW) and logical keys in
  // the map so layout-independent bindings work even when another
  // focused element steals the `code` at entry time.
  private readonly keys = new Set<string>();
  /** Keys that transitioned from up→down this frame. Drained on each
   *  `update(dt)` after edge-triggered actions (currently just jump)
   *  have been processed. Needed because holding Space would otherwise
   *  auto-bunny-hop the moment we touch ground; we want one jump per
   *  press. */
  private readonly justPressed = new Set<string>();

  private yaw = 0;
  private pitch = 0;

  private readonly raycaster = new Raycaster();
  private readonly crosshairNdc = new Vector2(0, 0);
  private lastHintId: string | null = null;
  /** The hinge currently under the crosshair; cached for E-press. */
  private focusedHinge: { id: string; def: HingeDef; group: Group } | null = null;

  private readonly onKeyDown = (e: KeyboardEvent) => {
    if (!this.active) return;
    // Ignore keystrokes while a form control is focused (e.g. the
    // seed field). Prevents "WASD" from getting typed into an input
    // when the user Alt-Tabs back to the page with a stale focus.
    const target = e.target as Element | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
      return;
    }
    // Edge detection: record only on the true up→down transition so
    // the auto-repeat that OSes fire while a key is held doesn't
    // re-latch the justPressed set.
    if (!this.keys.has(e.code)) {
      this.justPressed.add(e.code);
    }
    this.keys.add(e.code);
    if (e.code === 'KeyE') {
      e.preventDefault();
      e.stopPropagation();
      this.toggleFocusedHinge();
    }
    // Space would otherwise scroll the page even under pointer lock on
    // some browsers and would also get delivered to a focused button
    // that re-toggles ego mode; swallow it here.
    if (e.code === 'Space') {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  private readonly onKeyUp = (e: KeyboardEvent) => {
    this.keys.delete(e.code);
  };
  private readonly onMouseMove = (e: MouseEvent) => {
    if (!this.active) return;
    if (document.pointerLockElement !== this.domElement) return;
    const sensitivity = 0.0022;
    this.yaw -= e.movementX * sensitivity;
    this.pitch -= e.movementY * sensitivity;
    if (this.pitch > PITCH_LIMIT) this.pitch = PITCH_LIMIT;
    if (this.pitch < -PITCH_LIMIT) this.pitch = -PITCH_LIMIT;
  };
  /** Guards against the pointer-lock change that fires WHILE we're
   *  still in the middle of enter() (the initial acquisition). Without
   *  this, a browser that fires the event before `pointerLockElement`
   *  is swapped would trip the `!= domElement` branch and immediately
   *  kick us back out of ego mode. */
  private suppressLockExit = false;

  private readonly onPointerLockChange = () => {
    if (this.suppressLockExit) return;
    // Browser released the lock (ESC or user-driven); tell the listener
    // so React state can mirror the exit.
    if (document.pointerLockElement !== this.domElement && this.active) {
      this.onExitRequested?.();
    }
  };

  /** Fired when the browser releases pointer lock (i.e. the user hit
   *  ESC) so the viewport can flip React state back. */
  onExitRequested?: () => void;

  constructor(domElement: HTMLElement, scene: Scene, aspect: number) {
    this.domElement = domElement;
    this.scene = scene;
    this.camera = new PerspectiveCamera(70, aspect, 0.05, 200);
    this.camera.rotation.order = 'YXZ';

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('pointerlockchange', this.onPointerLockChange);
  }

  setOctree(octree: Octree | null): void {
    this.octree = octree;
  }

  resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /** Spawn the player and attempt to lock the pointer. Pointer lock
   *  request is gesture-gated by the browser so this must be called
   *  from within a user-gesture handler (the ViewModes button). */
  enter(spawn: EgoSpawn): void {
    this.active = true;
    this.keys.clear();
    this.justPressed.clear();
    this.velocity.set(0, 0, 0);
    this.onGround = false;
    // Start with the coyote timer "fresh" — the spawn point is always
    // on a surface, and we want the first Space press after entering
    // to jump even if the capsule takes a frame to resolve onto the
    // spawn floor.
    this.timeSinceGrounded = 0;
    this.yaw = spawn.yaw;
    this.pitch = 0;
    this.placeCapsule(spawn.x, spawn.y, spawn.z);
    this.syncCameraToCapsule();
    // The button that flipped ego mode on keeps DOM focus after the
    // click — which means stray keystrokes (Space, Enter) would be
    // delivered to it and re-toggle ego mode before the pointer-lock
    // handler ever sees them. Dropping focus on <body> keeps the
    // browser honest.
    if (document.activeElement instanceof HTMLElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
    // During the next microtask tick, any stray pointerlockchange (from
    // a previously-lost lock or React double-invoke in StrictMode)
    // should be ignored. We release the suppression on the next frame,
    // by which time the browser's real acquisition event has either
    // fired (and been suppressed) or been overtaken by the real lock.
    this.suppressLockExit = true;
    requestAnimationFrame(() => {
      this.suppressLockExit = false;
    });
    this.requestPointerLock();
  }

  exit(): void {
    if (!this.active) return;
    this.active = false;
    this.keys.clear();
    this.justPressed.clear();
    this.focusedHinge = null;
    if (this.lastHintId !== null) {
      useUiStore.getState().setInteractHint(null);
      this.lastHintId = null;
    }
    if (document.pointerLockElement === this.domElement) {
      document.exitPointerLock();
    }
  }

  update(dt: number): void {
    if (!this.active) return;
    // Cap dt so a big frame hitch doesn't teleport the player through a
    // wall. 50 ms is a generous ceiling (below it the normal physics
    // step still runs; above it we just slow the sim without causing
    // tunnelling).
    const step = Math.min(0.05, Math.max(0, dt));

    this.integrateVelocity(step);
    this.moveAndResolve(step);
    // Coyote timer: reset to 0 while grounded, otherwise tick up by
    // the frame's step. The JUMP check (integrateVelocity) reads it
    // on the NEXT frame, which is why we update it AFTER move/resolve
    // has finalised this frame's `onGround`.
    if (this.onGround) {
      this.timeSinceGrounded = 0;
    } else {
      this.timeSinceGrounded += step;
    }
    // Drain edge-triggered presses after this frame's logic has had a
    // chance to consume them.
    this.justPressed.clear();
    this.syncCameraToCapsule();
    this.updateCrosshair();
  }

  dispose(): void {
    this.exit();
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
  }

  // -- internals ---------------------------------------------------

  private placeCapsule(x: number, y: number, z: number): void {
    // Capsule convention: `start` is the centre of the bottom hemisphere,
    // `end` is the centre of the top hemisphere, `radius` rounds both
    // caps. For feet-on-floor we set start.y = radius so the bottom
    // hemisphere just kisses the ground; the top is at start + cylinder,
    // so total crown height is start + cylinder + radius (hemisphere).
    this.capsule.start.set(x, y + CAPSULE_RADIUS, z);
    this.capsule.end.set(x, y + CAPSULE_RADIUS + CYLINDER_HEIGHT, z);
  }

  private syncCameraToCapsule(): void {
    // Eye height = top of cylinder = capsule.end.y (1.30 m standing).
    // Camera sits inside the top hemisphere, which means the crown
    // sticks up another CAPSULE_RADIUS above the camera — giving us
    // the door clearance margin documented on CAPSULE_RADIUS /
    // CYLINDER_HEIGHT while keeping a natural sightline.
    this.camera.position.set(
      this.capsule.end.x,
      this.capsule.end.y,
      this.capsule.end.z,
    );
    // YXZ: yaw around world Y, then pitch around local X, no roll.
    this.camera.rotation.set(this.pitch, this.yaw, 0, 'YXZ');
  }

  private integrateVelocity(dt: number): void {
    // Horizontal basis from yaw only — pitch must not leak into walking
    // direction, or looking up would slow the player down.
    const forwardX = -Math.sin(this.yaw);
    const forwardZ = -Math.cos(this.yaw);
    const rightX = Math.cos(this.yaw);
    const rightZ = -Math.sin(this.yaw);

    let fwd = 0;
    let strafe = 0;
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) fwd += 1;
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) fwd -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) strafe += 1;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) strafe -= 1;

    const sprint = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
    const speed = WALK_SPEED * (sprint ? SPRINT_MULT : 1);

    let dx = forwardX * fwd + rightX * strafe;
    let dz = forwardZ * fwd + rightZ * strafe;
    const len = Math.hypot(dx, dz);
    if (len > 1e-6) {
      dx /= len;
      dz /= len;
    }

    // Horizontal velocity is authoritative (no inertia) — shot-for-shot
    // the same response every frame, which feels right for an
    // exploration mode with zero-gravity lateral control.
    this.velocity.x = dx * speed;
    this.velocity.z = dz * speed;

    // Jump: edge-triggered from Space. The grounded test is relaxed
    // via coyote time — we require `onGround` OR "grounded within the
    // last COYOTE_TIME seconds", which in practice makes Space feel
    // responsive even when the player triggers it during a 1-2 frame
    // airborne blip between stair treads or off a terrace lip. A
    // positive impulse lifts us off the ground; gravity does the
    // rest. Both `onGround` and the coyote timer are cleared so the
    // same press can't accidentally trigger two jumps.
    const groundedForJump =
      this.onGround || this.timeSinceGrounded <= COYOTE_TIME;
    if (groundedForJump && this.justPressed.has('Space')) {
      this.velocity.y = JUMP_IMPULSE;
      this.onGround = false;
      this.timeSinceGrounded = Number.POSITIVE_INFINITY;
    }

    // Vertical is integrated with gravity every frame; grounded-clamp
    // happens after collision resolution so the accumulation doesn't
    // pour into a slab.
    this.velocity.y += GRAVITY * dt;
    if (this.velocity.y < MAX_FALL) this.velocity.y = MAX_FALL;
  }

  /**
   * Advance the capsule by this frame's velocity in two independent
   * phases:
   *   1. Vertical sweep — resolves gravity / jumping against floors
   *      and ceilings, and (re)determines `onGround`.
   *   2. Horizontal sweep — slides along walls. If the horizontal
   *      motion was blocked AND the capsule was grounded before the
   *      sweep, we try to CLIMB the obstacle in the same frame:
   *        a. Revert to pre-sweep.
   *        b. Lift the capsule up to `STEP_HEIGHT`.
   *        c. Probe forward by `max(frame motion, STEP_PROBE_DIST)` —
   *           one frame (~5 cm) is not enough to cross a stair-tread
   *           leading edge, so we probe a hair over one capsule-radius
   *           which guarantees the sphere centre reaches the far side
   *           of whatever the capsule was pressed against. BAIL out if
   *           a non-floor-like surface is in the way (door header,
   *           ceiling, overhang, solid wall) — otherwise the probe
   *           would become a wall-hop.
   *        d. Sweep the capsule straight down up to `STEP_HEIGHT+ε`,
   *           resolving collisions as we go so we land on top of the
   *           step within the same frame (no mid-air "float" frame
   *           that produces visible jitter on stairs).
   *        e. If the final capsule didn't actually rise, or the
   *           horizontal component ended up opposing the intended
   *           heading, the "step" was a wall — revert to the
   *           wall-stopped position.
   *
   * Separating the axes this way is what makes stairs climbable at
   * all — a single combined sweep would push the capsule back out
   * along the riser's horizontal normal every frame instead of letting
   * it rise.
   */
  private moveAndResolve(dt: number): void {
    const tree = this.octree;

    // -- vertical phase -------------------------------------------------
    this.capsule.translate(new Vector3(0, this.velocity.y * dt, 0));
    this.onGround = false;
    if (tree) {
      this.pushOutOfGeometry(tree);
    }

    // Snapshot grounded state BEFORE the horizontal sweep — the push
    // below might flip `onGround` when we collide with a wall's base
    // and the step-up gate uses the pre-sweep state to decide whether
    // to try climbing.
    const wasGrounded = this.onGround;

    // -- horizontal phase ----------------------------------------------
    const hx = this.velocity.x * dt;
    const hz = this.velocity.z * dt;
    const attemptedSq = hx * hx + hz * hz;
    if (attemptedSq > 1e-10 && tree) {
      const preSweepStart = this.capsule.start.clone();
      const preSweepEnd = this.capsule.end.clone();
      this.capsule.translate(new Vector3(hx, 0, hz));
      this.pushOutOfGeometry(tree);

      // "Blocked" means the sphere did not make meaningful progress in
      // the DIRECTION we asked for. We compare a dot-product of the
      // achieved displacement onto the intended motion: stair-tread
      // hits (back when stairs were individual treads) produced pushes
      // with a diagonal normal that fooled the squared-length test, so
      // we look at actual forward progress instead.
      const dxA = this.capsule.start.x - preSweepStart.x;
      const dzA = this.capsule.start.z - preSweepStart.z;
      const dyFromPush = this.capsule.start.y - preSweepStart.y;
      const progress = dxA * hx + dzA * hz;
      // If the horizontal pushOut gave us noticeable vertical lift
      // (> 2 cm), we're on a RAMP — the ego spawn's plinth wall lifts
      // 0 mm (normal purely horizontal), but any slope builder (stair
      // ramp proxies, terrain rises) lifts a meaningful fraction of
      // the radius per frame. On ramps the solver is ALREADY resolving
      // the climb correctly via the capsule-vs-slope push, so
      // triggering step-up there would double-boost forward motion
      // and the player would rocket up every ramp. We therefore
      // suppress step-up whenever the horizontal phase produced lift.
      const onSlope = dyFromPush > 0.02;
      const blocked = !onSlope && progress < attemptedSq * 0.9;

      if (blocked && wasGrounded) {
        const wallStoppedStart = this.capsule.start.clone();
        const wallStoppedEnd = this.capsule.end.clone();
        const wallStoppedOnGround = this.onGround;

        // Forward probe distance for the step-up: MAX of this frame's
        // intended motion and STEP_PROBE_DIST. A single frame at walk
        // speed moves ~0.05 m — nowhere near enough to push the sphere
        // CENTRE past a stair-tread leading edge, so a sweep-down from
        // the lifted position would land back on the original floor
        // (classic "jiggle at the foot of the stairs"). See
        // STEP_PROBE_DIST for the full derivation.
        const hLen = Math.sqrt(attemptedSq);
        const probe = Math.max(hLen, STEP_PROBE_DIST);
        const probeX = (hx / hLen) * probe;
        const probeZ = (hz / hLen) * probe;

        // Revert + lift + probe forward.
        this.capsule.start.copy(preSweepStart);
        this.capsule.end.copy(preSweepEnd);
        this.capsule.translate(new Vector3(0, STEP_HEIGHT, 0));
        this.capsule.translate(new Vector3(probeX, 0, probeZ));

        // If lifting bumps the capsule into a non-floor-like surface
        // (door header, ceiling, overhang, solid wall ahead), the
        // floors-only push-out returns false and we revert. This is
        // what keeps the capsule from wedging itself into a door
        // frame when crossing a threshold, and what prevents the
        // longer step-probe from turning into a wall-hop.
        const cleared = this.pushOutOfGeometry(tree, /*floorsOnly=*/ true);
        if (!cleared) {
          this.capsule.start.copy(wallStoppedStart);
          this.capsule.end.copy(wallStoppedEnd);
          this.onGround = wallStoppedOnGround;
        } else {
          // Drop straight down by at most STEP_HEIGHT + a small
          // overshoot and resolve. This lands us on top of the step
          // within the same frame; without it the capsule floats at
          // the lifted height until the next gravity tick, which
          // shows up as per-frame vertical jitter on stairs.
          const DROP_OVERSHOOT = 0.05;
          this.onGround = false;
          this.capsule.translate(new Vector3(0, -(STEP_HEIGHT + DROP_OVERSHOOT), 0));
          this.pushOutOfGeometry(tree);
          // Verify we actually climbed AND moved in the intended
          // direction. Any `+0.01 m` rise above the pre-sweep height
          // is enough to count as a climb; anything less means the
          // step was a wall and gravity dropped us back. We also
          // require the horizontal progress to be non-negative —
          // we'd rather leave the player stuck at a wall than
          // silently reverse their heading.
          const climbedBy = this.capsule.start.y - preSweepStart.y;
          const climbDx = this.capsule.start.x - preSweepStart.x;
          const climbDz = this.capsule.start.z - preSweepStart.z;
          const climbProgress = climbDx * hx + climbDz * hz;
          if (climbedBy <= 0.01 || climbProgress < 0) {
            this.capsule.start.copy(wallStoppedStart);
            this.capsule.end.copy(wallStoppedEnd);
            this.onGround = wallStoppedOnGround;
          }
        }
      }
    }

    // Clamp vertical velocity while grounded so gravity doesn't
    // accumulate into a slab.
    if (this.onGround && this.velocity.y < 0) this.velocity.y = 0;
  }

  /**
   * Iteratively push the capsule out of any geometry it overlaps,
   * updating `onGround` along the way.
   *
   * When `floorsOnly` is true we will only absorb floor-like
   * (normal.y > 0.7) pushes — useful for the step-up's raised sweep,
   * where we want to let the capsule "land" on the top of a stair
   * but must bail out if there's a wall blocking the forward path.
   * Returns `true` if every iteration either found no overlap or was
   * resolvable under the current policy; `false` if a blocking
   * (non-floor) overlap was encountered while floorsOnly was true.
   */
  private pushOutOfGeometry(tree: Octree, floorsOnly = false): boolean {
    // Multiple passes so that when two triangles pinch the capsule we
    // don't leave a residual overlap that the next frame has to clean
    // up (looks like a tremor against corners). Four passes is plenty
    // for the concave indoor geometry we see here.
    for (let i = 0; i < 4; i++) {
      const result = tree.capsuleIntersect(this.capsule);
      if (!result) return true;
      if (floorsOnly && result.normal.y <= 0.7) {
        return false;
      }
      this.capsule.translate(result.normal.multiplyScalar(result.depth));
      if (result.normal.y > 0.3) {
        this.onGround = true;
        if (this.velocity.y < 0) this.velocity.y = 0;
      }
    }
    return true;
  }

  private updateCrosshair(): void {
    // Raycast from camera forward; first triangle-bearing hinge along
    // the line wins. We cache the hinge group on the matching mesh's
    // ancestor chain so E can toggle without re-raycasting.
    this.raycaster.setFromCamera(this.crosshairNdc, this.camera);
    this.raycaster.far = INTERACT_REACH;
    const hits = this.raycaster.intersectObject(this.scene, true);
    let found: { id: string; def: HingeDef; group: Group } | null = null;
    for (const hit of hits) {
      let ancestor: Object3D | null = hit.object;
      while (ancestor) {
        if (
          ancestor instanceof Group &&
          ancestor.name?.startsWith('Hinge_') &&
          ancestor.userData?.hinge
        ) {
          const def = ancestor.userData.hinge as HingeDef;
          found = { id: def.id, def, group: ancestor };
          break;
        }
        ancestor = ancestor.parent;
      }
      if (found) break;
    }
    this.focusedHinge = found;
    const uiStore = useUiStore.getState();
    if (found) {
      const values = useHingesStore.getState().values;
      const t = values[found.id] ?? 0;
      const verb: EgoInteractHint['verb'] = t < 0.5 ? 'Open' : 'Close';
      if (
        this.lastHintId !== found.id ||
        uiStore.interactHint?.verb !== verb
      ) {
        uiStore.setInteractHint({ id: found.id, label: found.def.label, verb });
        this.lastHintId = found.id;
      }
    } else if (this.lastHintId !== null) {
      uiStore.setInteractHint(null);
      this.lastHintId = null;
    }
  }

  private toggleFocusedHinge(): void {
    const focus = this.focusedHinge;
    if (!focus) return;
    const store = useHingesStore.getState();
    const current = store.values[focus.id] ?? 0;
    const next = current < 0.5 ? 1 : 0;
    // Single source of truth: setValue fires the zustand subscription
    // in App.tsx, which forwards the new target into the viewport's
    // hinge-animation table. The leaf tweens to the new position over
    // the next few frames — crucially, the octree does NOT change
    // (hinge leaves are excluded from collision in all states), so
    // this is pure visual and can never throw the player out of ego
    // mode mid-swing.
    store.setValue(focus.id, next);
  }

  private requestPointerLock(): void {
    // Wrapped so we can swallow any "user denied" exceptions quietly.
    const el = this.domElement as HTMLElement & { requestPointerLock?: () => Promise<void> | void };
    try {
      const ret = el.requestPointerLock?.();
      if (ret && typeof (ret as Promise<void>).catch === 'function') {
        (ret as Promise<void>).catch(() => void 0);
      }
    } catch {
      // No-op: ego mode still works, just without mouse look until the
      // user clicks the viewport to lock manually.
    }
  }
}
